import { extractIdFromGid } from '@theme/global-utilities';

/**
 * Client-side Regios (DOPP) discount pricing service.
 *
 * Talks to the Regios app's in-page API (`window.RegiosDOPP.api.v0`) to compute
 * campaign discounts for a product/variant, and exposes small pure helpers used
 * by the PDP and product-card surfaces to decide what to render.
 */

/**
 * @typedef {Object} RegiosDoppPrice
 * @property {number} amount
 * @property {string} formatted
 */

/**
 * @typedef {Object} RegiosDoppDescription
 * @property {string} html
 * @property {string} css
 */

/**
 * @typedef {Object} RegiosDoppCalculateResult
 * @property {RegiosDoppPrice} salePrice
 * @property {RegiosDoppPrice} regularPrice
 * @property {RegiosDoppDescription} description
 * @property {RegiosDoppDescription} badge
 * @property {string=} discountMessage
 */

/**
 * Normalized input shared by every caller. PDP passes cents
 * (`regularPriceInCents`); cards pass major-unit strings (`price`). The service
 * converts to cents internally so there is a single unit contract on the wire.
 *
 * @typedef {Object} RegiosPriceInput
 * @property {string|number|null|undefined} productId
 * @property {string|number|null|undefined} variantId
 * @property {number|null|undefined} [regularPriceInCents]
 * @property {number|null|undefined} [compareAtPriceInCents]
 * @property {string|number|null|undefined} [price] - major units (dollars)
 * @property {string|number|null|undefined} [compareAtPrice] - major units (dollars)
 * @property {Array<string|number>|null|undefined} [collectionIds]
 * @property {Array<string>|string|null|undefined} [tags]
 * @property {string|null|undefined} [vendor]
 * @property {string|null|undefined} [handle]
 * @property {string|null|undefined} [url]
 * @property {string|null|undefined} [variantTitle]
 * @property {boolean} [isOutOfStock]
 */

/**
 * @typedef {Object} RegiosDiscountCalculationOptions
 * @property {number=} quantity
 * @property {Object|null=} customer
 * @property {'FIRST'|'LAST'=} insertAt
 */

const REGIOS_DOPP_READY_EVENT = 'regios-dopp:api-initialized';
const REGIOS_DOPP_WAIT_TIMEOUT_MS = 10000;
/** Flip to true when debugging Regios responses locally. */
const DEBUG = false;

/**
 * Coerces a Shopify id or gid into its numeric id.
 * @param {string|number|null|undefined} value
 * @returns {number|null}
 */
function toNumericShopifyId(value) {
  if (value == null || value === '') return null;
  const fromGid = extractIdFromGid(value);
  if (fromGid) return Number(fromGid);
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.trunc(numeric) : null;
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function toInteger(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }
  return null;
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function asFiniteNumber(value) {
  const numeric = typeof value === 'string' ? Number(value) : value;
  return typeof numeric === 'number' && Number.isFinite(numeric) ? numeric : null;
}

/**
 * Parses a major-unit (dollars) value into integer cents.
 * Accepts a number (39.99) or a formatted/plain string ("$39.99", "39,99").
 * @param {unknown} value
 * @returns {number|null}
 */
function parsePriceToCents(value) {
  if (value == null || value === '') return null;
  let dollars;
  if (typeof value === 'number') {
    dollars = value;
  } else {
    const cleaned = String(value).replace(/[^\d.,-]/g, '').replace(',', '.');
    dollars = parseFloat(cleaned);
  }
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : null;
}

/**
 * @param {Array<string|number>|null|undefined} value
 * @returns {number[]}
 */
function normalizeCollectionIds(value) {
  if (!Array.isArray(value)) return [];
  return /** @type {number[]} */ (
    value.map((entry) => toNumericShopifyId(entry)).filter((id) => id != null)
  );
}

/**
 * @param {Array<string>|string|null|undefined} value
 * @returns {string[]}
 */
function normalizeTags(value) {
  if (Array.isArray(value)) {
    return value.map((tag) => String(tag).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(',').map((tag) => tag.trim()).filter(Boolean);
  }
  return [];
}

class RegiosProductCardPricing {
  constructor() {
    /** @type {Promise<any | null> | null} */
    this.apiWaitPromise = null;
  }

  /**
   * @returns {any | null}
   */
  getApi() {
    return window?.RegiosDOPP?.api?.v0 || null;
  }

  /**
   * Resolves once the Regios API is available, or after a timeout.
   * @param {number=} timeoutMs
   * @returns {Promise<any | null>}
   */
  waitForApi(timeoutMs = REGIOS_DOPP_WAIT_TIMEOUT_MS) {
    const alreadyReady = this.getApi();
    if (alreadyReady) return Promise.resolve(alreadyReady);
    if (this.apiWaitPromise) return this.apiWaitPromise;

    this.apiWaitPromise = new Promise((resolve) => {
      let isResolved = false;
      /** @type {number|null} */
      let timeoutId = null;

      const cleanup = () => {
        window.removeEventListener(REGIOS_DOPP_READY_EVENT, onInitialized);
        if (timeoutId !== null) window.clearTimeout(timeoutId);
      };

      const finalize = (/** @type {any} */ api) => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        this.apiWaitPromise = null;
        resolve(api || null);
      };

      const onInitialized = (/** @type {any} */ event) => {
        finalize(event?.detail?.api || this.getApi());
      };

      window.addEventListener(REGIOS_DOPP_READY_EVENT, onInitialized, { once: true });
      timeoutId = window.setTimeout(() => finalize(this.getApi()), timeoutMs);
    });

    return this.apiWaitPromise;
  }

  /**
   * @param {Object|null|undefined} customerOverride
   * @returns {Object|null|undefined}
   */
  getCustomer(customerOverride) {
    if (customerOverride !== undefined) return customerOverride;
    return window.RegiosDOPPCustomerLiquid || undefined;
  }

  /**
   * Maps the normalized input into the DOPP product payload.
   * @param {RegiosPriceInput | null} input
   * @returns {Record<string, any> | null}
   */
  mapToDoppProduct(input) {
    const productId = toNumericShopifyId(input?.productId);
    const variantId = toNumericShopifyId(input?.variantId);
    const regularPriceInCents =
      toInteger(input?.regularPriceInCents) ?? parsePriceToCents(input?.price);

    if (!productId || !variantId || regularPriceInCents == null) {
      return null;
    }

    const compareAtPriceInCents =
      toInteger(input?.compareAtPriceInCents) ?? parsePriceToCents(input?.compareAtPrice);

    const variant = {
      id: variantId,
      title: input?.variantTitle || '',
      priceInCents: regularPriceInCents,
      compareAtPriceInCents: compareAtPriceInCents ?? null,
      isOutOfStock: input?.isOutOfStock === true,
    };

    /** @type {Record<string, any>} */
    const payload = {
      productId,
      variantId,
      regularPriceInCents,
      collectionIds: normalizeCollectionIds(input?.collectionIds),
      tags: normalizeTags(input?.tags),
      variants: [variant],
    };

    if (compareAtPriceInCents != null && compareAtPriceInCents > 0) {
      payload.compareAtPriceInCents = compareAtPriceInCents;
    }
    if (input?.handle) payload.handle = input.handle;
    if (input?.vendor) payload.vendor = input.vendor;
    if (input?.url) payload.url = input.url;

    return payload;
  }

  /**
   * @param {RegiosPriceInput | null} input
   * @param {RegiosDiscountCalculationOptions=} options
   * @returns {Promise<RegiosDoppCalculateResult | null>}
   */
  async calculateDiscountedPrices(input, options = {}) {
    const api = this.getApi() || (await this.waitForApi());
    if (!api || typeof api.calculateDiscountedPrices !== 'function') {
      return null;
    }

    const payload = this.mapToDoppProduct(input);
    if (!payload) return null;

    /** @type {Record<string, any>} */
    const args = { ...payload, quantity: options.quantity || 1 };

    if (options.insertAt === 'FIRST' || options.insertAt === 'LAST') {
      args.insertAt = options.insertAt;
    }

    const customer = this.getCustomer(options.customer);
    if (customer !== undefined) args.customer = customer;

    try {
      const result = await api.calculateDiscountedPrices(args);
      if (DEBUG) {
        console.info('[Regios] DOPP price response', {
          productId: payload.productId,
          variantId: payload.variantId,
          result,
        });
      }
      return result || null;
    } catch (error) {
      if (DEBUG) console.warn('[Regios] Failed to calculate discounted price', error);
      return null;
    }
  }
}

export const regiosPricing = new RegiosProductCardPricing();

/**
 * Page-lifetime dedupe cache keyed by `${productId}::${variantId}`.
 * Storing the in-flight promise guarantees exactly one network call per variant,
 * even when several consumers (PDP price + sticky ATC + badge, or repeated cards)
 * request the same product/variant.
 * @type {Map<string, Promise<RegiosDoppCalculateResult | null>>}
 */
const priceCache = new Map();

/**
 * Memoized entry point used by every surface.
 * @param {RegiosPriceInput | null} input
 * @param {RegiosDiscountCalculationOptions=} options
 * @returns {Promise<RegiosDoppCalculateResult | null>}
 */
export function getDiscountedPrices(input, options) {
  const productId = toNumericShopifyId(input?.productId);
  const variantId = toNumericShopifyId(input?.variantId);
  if (!productId || !variantId) return Promise.resolve(null);

  const key = `${productId}::${variantId}`;
  const cached = priceCache.get(key);
  if (cached) return cached;

  const promise = regiosPricing
    .calculateDiscountedPrices(input, options)
    .then((result) => {
      if (result == null) priceCache.delete(key);
      return result;
    })
    .catch(() => {
      priceCache.delete(key);
      return null;
    });
  priceCache.set(key, promise);
  return promise;
}

/**
 * True when Regios lowered this variant's price (the price-display signal).
 * @param {RegiosDoppCalculateResult | null | undefined} result
 * @returns {boolean}
 */
export function isRegiosDiscount(result) {
  const sale = asFiniteNumber(result?.salePrice?.amount);
  const regular = asFiniteNumber(result?.regularPrice?.amount);
  return sale != null && regular != null && sale < regular;
}

/**
 * Applies a Regios result to the shared `price` snippet markup: hides the
 * regular group, reveals the sale group, writes the formatted sale/compare
 * prices. No-ops when there is no discount or the markup is missing.
 * @param {Element|null|undefined} priceContainer
 * @param {RegiosDoppCalculateResult | null | undefined} result
 * @returns {boolean} true when the swap happened
 */
export function applyRegiosSalePrices(priceContainer, result) {
  if (!priceContainer || !result || !isRegiosDiscount(result)) return false;

  const saleGroup = priceContainer.querySelector('.price__sale');
  const saleEl = priceContainer.querySelector('.price-item--sale.price');
  const compareEl = priceContainer.querySelector('.price-item--regular.compare-at-price');
  if (!saleGroup || !saleEl || !compareEl) return false;

  priceContainer.querySelector('.price__regular')?.classList.add('price__hidden');
  saleGroup.classList.remove('price__hidden');
  saleEl.textContent = result.salePrice.formatted;
  compareEl.textContent = result.regularPrice.formatted;
  return true;
}

/**
 * Derives the Regios sale price as integer cents for consumers that need to do
 * math on it (e.g. the add-to-cart button's `unit price x quantity` label).
 * The DOPP response documents `amount` but not its unit, so the unit is
 * inferred by matching `regularPrice.amount` against the native price we sent
 * (in cents). No match (e.g. a compare-at based regular) returns null so the
 * caller keeps the native price instead of rendering wrong math.
 * @param {RegiosDoppCalculateResult | null | undefined} result
 * @param {number|null|undefined} regularPriceInCents - The native variant price sent to Regios.
 * @returns {number|null}
 */
export function getRegiosSaleCents(result, regularPriceInCents) {
  const sale = asFiniteNumber(result?.salePrice?.amount);
  const regular = asFiniteNumber(result?.regularPrice?.amount);
  const regularCents = toInteger(regularPriceInCents);
  if (sale == null || regular == null || regularCents == null || regularCents <= 0) return null;
  if (Math.abs(regular - regularCents) < 0.5) return Math.round(sale);
  if (Math.abs(regular * 100 - regularCents) < 0.5) return Math.round(sale * 100);
  return null;
}

/**
 * The plain-text discount title for the badge (never HTML from Regios).
 * @param {RegiosDoppCalculateResult | null | undefined} result
 * @returns {string}
 */
export function getRegiosDiscountTitle(result) {
  const message = result?.discountMessage;
  return typeof message === 'string' ? message.trim() : '';
}

/**
 * Reads the per-product Regios context JSON emitted once on the PDP
 * (`<script type="application/json" id="regios-context-{id}">`).
 * @param {string|number} productId
 * @returns {any}
 */
export function readRegiosPdpContext(productId) {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(`regios-context-${productId}`);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || '{}');
  } catch {
    return null;
  }
}

/**
 * Builds the normalized Regios input for a PDP variant from the emitted context.
 * Both PDP consumers (price + badge) call this, so their inputs are identical —
 * the `(productId, variantId)` cache entry is populated the same way regardless
 * of which element resolves first (one network call, order-independent).
 * @param {string|number|null|undefined} productId
 * @param {string|number|null|undefined} variantId
 * @returns {RegiosPriceInput|null}
 */
export function buildRegiosPdpInputs(productId, variantId) {
  if (productId == null || variantId == null || variantId === '') return null;
  const ctx = readRegiosPdpContext(productId);
  const variants = (ctx && ctx.variants) || {};
  const variant = variants[String(variantId)] || {};
  return {
    productId,
    variantId,
    regularPriceInCents: variant.priceInCents,
    compareAtPriceInCents: variant.compareAtPriceInCents,
    collectionIds: ctx ? ctx.collectionIds : undefined,
    tags: ctx ? ctx.tags : undefined,
    vendor: ctx ? ctx.vendor : undefined,
  };
}

/**
 * Builds the normalized Regios input from an enriched product-card's data (the
 * card counterpart to `buildRegiosPdpInputs`). Cards carry major-unit price
 * strings; the module converts them to cents. Returns null when there is no
 * usable variant/price, so callers can show the native price and skip the call.
 * @param {import('@theme/product-card-helpers').ProductCardData | null | undefined} productData
 * @returns {RegiosPriceInput | null}
 */
export function buildRegiosCardInputs(productData) {
  const variant = productData?.selectedVariant;
  const variantId = variant?.id ?? productData?.selectedVariantId;
  const price = variant?.price ?? productData?.price;
  if (!productData?.id || variantId == null || price == null) return null;

  return {
    productId: productData.id,
    variantId,
    price,
    compareAtPrice: variant?.compareAtPrice ?? productData.compareAtPrice,
    collectionIds: productData.collectionIds,
    tags: productData.tags,
    handle: productData.handle,
    url: productData.url,
    isOutOfStock: variant ? variant.availableForSale === false : undefined,
  };
}

if (typeof window !== 'undefined') {
  /** @type {any} */ (window).RegiosProductCardPricing = RegiosProductCardPricing;
  if (!(/** @type {any} */ (window).regiosProductCardPricing)) {
    /** @type {any} */ (window).regiosProductCardPricing = regiosPricing;
  }
}
